/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: 管理员专属页面，通过邀请码邀请用户注册，并可删除老用户
 * Style referenceFiles:
 * Design for: Mobile
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - 生成邀请码（8 位字母数字，一次性使用）
 * - 复制邀请文案到剪贴板
 * - 删除未使用的邀请码
 * - 查看用户列表、删除用户
 * - 导航: 返回设置页
 *
 * ## Reference
 * - canvas/[Feature] UserManagement/feature-brief.md
 * </page-design>
 */

import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { toast } from '@/components/function/base/Toaster'
import * as UserManagementStore from '../../stores/UserManagementStore'
import * as InvitationStore from '../../stores/InvitationStore'
import * as LoginStore from '../../stores/LoginStore'

function UserManagement() {
  const { data: usersData } = useAtomValue(UserManagementStore.refreshAtom)
  const { data: invitationsData } = useAtomValue(InvitationStore.refreshAtom)
  const deleteUser = useSetAtom(UserManagementStore.deleteUserAtom)
  const createInvitation = useSetAtom(InvitationStore.createAtom)
  const deleteInvitation = useSetAtom(InvitationStore.deleteAtom)
  const currentUser = useAtomValue(LoginStore.currentUserAtom)
  const navigate = useNavigate()
  const [confirmDeleteUserId, setConfirmDeleteUserId] = useState<string | null>(null)
  const [confirmDeleteCode, setConfirmDeleteCode] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const users = usersData?.users ?? []
  const invitations = invitationsData?.invitations ?? []

  const handleCreateInvitation = async () => {
    setCreating(true)
    try {
      await createInvitation()
      toast.success('邀请码已生成')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '生成失败')
    } finally {
      setCreating(false)
    }
  }

  const handleCopy = async (code: string) => {
    const text = InvitationStore.buildInviteMessage(code)
    try {
      await navigator.clipboard.writeText(text)
      toast.success('邀请文案已复制')
    } catch {
      toast.error('复制失败，请手动复制')
    }
  }

  const handleDeleteInvitation = async (code: string) => {
    if (confirmDeleteCode === code) {
      try {
        await deleteInvitation(code)
        setConfirmDeleteCode(null)
        toast.success('邀请码已删除')
      } catch (e) {
        toast.error(e instanceof Error ? e.message : '删除失败')
      }
    } else {
      setConfirmDeleteCode(code)
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (confirmDeleteUserId === userId) {
      await deleteUser(userId)
      setConfirmDeleteUserId(null)
      toast.success('用户已删除')
    } else {
      setConfirmDeleteUserId(userId)
    }
  }

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#12141D' }}>
      {/* Header */}
      <div className="flex-none pt-12 pb-4 px-6 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#1C1F2E' }}
        >
          <Icon icon="lucide:arrow-left" className="w-5 h-5 text-gray-400" />
        </button>
        <h1 className="text-xl font-bold text-white">用户管理</h1>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto px-6 pb-6 flex flex-col gap-6">
        {/* Invitations Section */}
        <section>
          <h2 className="text-sm font-bold text-gray-500 mb-3 px-1 uppercase tracking-wider">邀请码</h2>
          <div className="rounded-2xl p-5 flex flex-col gap-4" style={{ backgroundColor: '#1C1F2E' }}>
            <button
              onClick={handleCreateInvitation}
              disabled={creating}
              className="w-full py-3 rounded-xl text-white font-bold text-sm border-b-[4px] active:border-b-0 active:translate-y-[4px] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#10B981', borderColor: '#059669' }}
            >
              <Icon icon="lucide:plus" className="w-4 h-4" />
              {creating ? '生成中...' : '生成邀请码'}
            </button>

            {invitations.length > 0 && (
              <div className="flex flex-col gap-3">
                {invitations.map((inv) => {
                  const isUsed = !!inv.usedAt
                  const isConfirmingDelete = confirmDeleteCode === inv.code
                  return (
                    <div
                      key={inv.code}
                      className="rounded-xl p-4 flex flex-col gap-2"
                      style={{ backgroundColor: '#12141D', border: '1px solid #2A2D3A' }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-white text-base font-bold tracking-widest">{inv.code}</span>
                        {isUsed ? (
                          <span className="text-xs text-gray-500 bg-gray-500/10 px-2 py-0.5 rounded-full font-bold">已使用</span>
                        ) : (
                          <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full font-bold">未使用</span>
                        )}
                      </div>

                      {isUsed ? (
                        <div className="text-xs text-gray-400">
                          {inv.usedByPhone ? InvitationStore.maskPhone(inv.usedByPhone) : '已注销用户'}
                          {' · '}
                          {inv.usedAt ? new Date(inv.usedAt).toLocaleString('zh-CN', { hour12: false }) : ''}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleCopy(inv.code)}
                            className="flex-1 py-2 rounded-lg text-xs font-bold bg-emerald-400/10 text-emerald-400 flex items-center justify-center gap-1"
                          >
                            <Icon icon="lucide:copy" className="w-3.5 h-3.5" />
                            复制邀请文案
                          </button>
                          <button
                            onClick={() => handleDeleteInvitation(inv.code)}
                            className={`px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1 ${
                              isConfirmingDelete ? 'bg-red-500 text-white' : 'bg-red-400/10 text-red-400'
                            }`}
                          >
                            <Icon icon="lucide:trash-2" className="w-3.5 h-3.5" />
                            {isConfirmingDelete ? '确认' : '删除'}
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>

        {/* User List */}
        <section>
          <h2 className="text-sm font-bold text-gray-500 mb-3 px-1 uppercase tracking-wider">所有用户</h2>
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1C1F2E' }}>
            {users.map((u, idx) => {
              const isSelf = u.id === currentUser?.id
              return (
                <div
                  key={u.id}
                  className={`flex items-center justify-between px-5 py-4 ${idx < users.length - 1 ? 'border-b border-[#2A2D3A]' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: '#12141D' }}
                    >
                      <Icon icon="lucide:user" className="w-4 h-4 text-gray-500" />
                    </div>
                    <div>
                      <span className="text-white font-medium text-sm">{u.phone}</span>
                      {isSelf && (
                        <span className="ml-2 text-xs text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full font-bold">
                          我
                        </span>
                      )}
                      {u.role === 'admin' && (
                        <span className="ml-1 text-xs text-violet-400 bg-violet-400/10 px-2 py-0.5 rounded-full font-bold">
                          管理员
                        </span>
                      )}
                    </div>
                  </div>
                  {!isSelf && (
                    <button
                      onClick={() => handleDeleteUser(u.id)}
                      className={`px-4 py-1.5 rounded-xl text-sm font-bold transition-all ${
                        confirmDeleteUserId === u.id
                          ? 'bg-red-500 text-white'
                          : 'text-red-400 bg-red-400/10'
                      }`}
                    >
                      {confirmDeleteUserId === u.id ? '确认删除' : '删除'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      </main>
    </div>
  )
}

export default UserManagement
